// Resolves the right Nav / Footer preset for the current page-type, with
// optional per-page override (preset_id wins over page-type assignment).
// Caches results by `${pageType}::${presetOverrideId}` to avoid duplicate
// requests across navigations.
import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { prepareNavLinks, clearPreparedNavLinksCache } from '@/pages/public/homepage/navLinkUtils';
import { getHomepageBootstrapPromise, prefetchHomepageBootstrap, isHomepagePath } from '@/utils/publicBootstrap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const navCache = new Map();
const footerCache = new Map();
const navInflight = new Map();
const footerInflight = new Map();

const cacheKey = (pageType, presetOverrideId) => `${pageType || ''}::${presetOverrideId || ''}`;

export function seedNavPresetCache(pageType, presetOverrideId, preset) {
  navCache.set(cacheKey(pageType, presetOverrideId), preset);
  navInflight.delete(cacheKey(pageType, presetOverrideId));
}

export function seedFooterPresetCache(pageType, presetOverrideId, preset) {
  footerCache.set(cacheKey(pageType, presetOverrideId), preset);
  footerInflight.delete(cacheKey(pageType, presetOverrideId));
}

export function useNavPreset(pageType, presetOverrideId = null) {
  const key = cacheKey(pageType, presetOverrideId);
  const [preset, setPreset] = useState(navCache.get(key) || null);
  const [loaded, setLoaded] = useState(navCache.has(key));

  useEffect(() => {
    const syncFromCache = () => {
      if (navCache.has(key)) {
        setPreset(navCache.get(key));
        setLoaded(true);
        return true;
      }
      return false;
    };

    if (syncFromCache()) {
      return undefined;
    }

    let alive = true;
    const onBootstrapReady = () => {
      if (alive) syncFromCache();
    };
    window.addEventListener('public-home-bootstrap-ready', onBootstrapReady);

    const loadPreset = async () => {
      if (isHomepagePath()) {
        const bootstrapPromise = getHomepageBootstrapPromise() || prefetchHomepageBootstrap();
        if (bootstrapPromise) {
          await bootstrapPromise;
          if (!alive) return;
          if (syncFromCache()) return;
        }
      }

      if (!alive || navCache.has(key)) return;

      if (!navInflight.has(key)) {
        const params = { page_type: pageType || '' };
        if (presetOverrideId) params.preset_id = presetOverrideId;
        const p = axios
          .get(`${BACKEND_URL}/api/site-settings/nav-presets/resolve`, { params })
          .then((r) => r.data?.preset || null)
          .catch(() => null);
        navInflight.set(key, p);
      }

      const d = await navInflight.get(key);
      navCache.set(key, d);
      navInflight.delete(key);
      if (alive) {
        setPreset(d);
        setLoaded(true);
      }
    };

    loadPreset();
    return () => {
      alive = false;
      window.removeEventListener('public-home-bootstrap-ready', onBootstrapReady);
    };
  }, [key, pageType, presetOverrideId]);

  const preparedLinks = useMemo(() => {
    if (!preset?.links) return [];
    const linksKey = `${key}::${preset.id || ''}::${preset.links.map((l) => l.id).join(',')}`;
    return prepareNavLinks(preset.links, linksKey);
  }, [key, preset?.id, preset?.links]);

  return { preset, loaded, preparedLinks };
}

export function useFooterPreset(pageType, presetOverrideId = null) {
  const key = cacheKey(pageType, presetOverrideId);
  const [preset, setPreset] = useState(footerCache.get(key) || null);
  const [loaded, setLoaded] = useState(footerCache.has(key));

  useEffect(() => {
    const syncFromCache = () => {
      if (footerCache.has(key)) {
        setPreset(footerCache.get(key));
        setLoaded(true);
        return true;
      }
      return false;
    };

    if (syncFromCache()) {
      return undefined;
    }

    let alive = true;
    const onBootstrapReady = () => {
      if (alive) syncFromCache();
    };
    window.addEventListener('public-home-bootstrap-ready', onBootstrapReady);

    const loadPreset = async () => {
      if (isHomepagePath()) {
        const bootstrapPromise = getHomepageBootstrapPromise() || prefetchHomepageBootstrap();
        if (bootstrapPromise) {
          await bootstrapPromise;
          if (!alive) return;
          if (syncFromCache()) return;
        }
      }

      if (!alive || footerCache.has(key)) return;

      if (!footerInflight.has(key)) {
        const params = { page_type: pageType || '' };
        if (presetOverrideId) params.preset_id = presetOverrideId;
        const p = axios
          .get(`${BACKEND_URL}/api/site-settings/footer-presets/resolve`, { params })
          .then((r) => r.data?.preset || null)
          .catch(() => null);
        footerInflight.set(key, p);
      }

      const d = await footerInflight.get(key);
      footerCache.set(key, d);
      footerInflight.delete(key);
      if (alive) {
        setPreset(d);
        setLoaded(true);
      }
    };

    loadPreset();
    return () => {
      alive = false;
      window.removeEventListener('public-home-bootstrap-ready', onBootstrapReady);
    };
  }, [key, pageType, presetOverrideId]);

  return { preset, loaded };
}

export function clearPresetCaches() {
  navCache.clear();
  footerCache.clear();
  navInflight.clear();
  footerInflight.clear();
  clearPreparedNavLinksCache();
}
